import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { ElementType } from '@/player-config/src/types/Element/Type';
import RangeSlider from '@/components/atoms/RangeSlider';
import { getAudioDuration } from '@/App/remotion/components/AudioElementComponent/utils/calculateAudioVolume';
import type { AudioElement } from '@/player-config/src/types/RemotionPlayerConfigTypes/Element/AudioElement';
import PercentageInput from '@/Pages/Clip/ClipPlayer/ClipAssets/AudioOverlay/PercentageInput';

interface AudioFadeControlsProps {
  element: {
    id: string;
    type: ElementType;
    fromFrame?: number;
    toFrame?: number;
    details?: {
      fadeIn?: number;
      fadeOut?: number;
      [key: string]: any;
    };
    [key: string]: any;
  };
  debouncedUpdate: (updates: any) => void;
}

const AudioFadeControls: React.FC<AudioFadeControlsProps> = ({ element, debouncedUpdate }) => {
  const [fadeInPercentage, setFadeInPercentage] = useState(0);
  const [fadeOutPercentage, setFadeOutPercentage] = useState(0);
  const { fps } = useClipsContext();

  const audioElement = element as AudioElement;

  const audioDuration = useMemo(() => {
    if (element.fromFrame === undefined || element.toFrame === undefined) {
      return 0;
    }
    return getAudioDuration(audioElement, fps);
  }, [audioElement, fps, element.fromFrame, element.toFrame]);

  useEffect(() => {
    setFadeInPercentage(element.details?.fadeIn ?? 0);
    setFadeOutPercentage(element.details?.fadeOut ?? 0);
  }, [element.details?.fadeIn, element.details?.fadeOut]);

  const handleFadeInChange = useCallback(
    (newFadeInPercentage: number) => {
      setFadeInPercentage(newFadeInPercentage);
      debouncedUpdate({
        details: {
          ...element.details,
          fadeIn: newFadeInPercentage
        }
      });
    },
    [debouncedUpdate, element.details]
  );

  const handleFadeOutChange = useCallback(
    (newFadeOutPercentage: number) => {
      setFadeOutPercentage(newFadeOutPercentage);
      debouncedUpdate({
        details: {
          ...element.details,
          fadeOut: newFadeOutPercentage
        }
      });
    },
    [debouncedUpdate, element.details]
  );

  if (audioDuration === 0) {
    return (
      <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
        <p className="text-xs text-gray-500">Fade controls unavailable - audio duration unknown</p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-slate-200 py-2">
      <div className="space-y-2.5">
        {/* Fade In Control */}
        <div className="flex items-center gap-1 px-2">
          <span className="w-16 shrink-0 text-sm text-gray-700">Fade in</span>
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <div className="min-w-0 flex-1">
              <RangeSlider
                id="audio-fade-in"
                min={0}
                max={100}
                step={1}
                value={fadeInPercentage}
                onChange={handleFadeInChange}
              />
            </div>
            <PercentageInput
              value={fadeInPercentage}
              onChange={handleFadeInChange}
              data-testid="audio-fade-in-percentage"
            />
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-slate-200"></div>

        {/* Fade Out Control */}
        <div className="flex items-center gap-1 px-2">
          <span className="w-16 shrink-0 text-sm text-gray-700">Fade out</span>
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <div className="min-w-0 flex-1">
              <RangeSlider
                id="audio-fade-out"
                min={0}
                max={100}
                step={1}
                value={fadeOutPercentage}
                onChange={handleFadeOutChange}
              />
            </div>
            <PercentageInput
              value={fadeOutPercentage}
              onChange={handleFadeOutChange}
              data-testid="audio-fade-out-percentage"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(AudioFadeControls);
